import { authRequest } from "@api/auth-requests";
import { AUTH_API_ROUTES, AUTH_PRODUCT } from "@constants/constants";
import { useMutation, useQueryClient } from "@tanstack/react-query";
import { handleApiError } from "@utils/handle-api-error";
import { tokenInstance } from "@utils/token-instance";
import type { AxiosError } from "axios";

import { useAuth } from "../utils/use-auth";
import { LOGIN_QUERY_KEY } from "./use-login-request";

interface UpdateTempPasswordRequest {
	login: string;
	/** The temporary password the user signed in with. */
	current_password: string;
	new_password: string;
	confirm_password: string;
}

interface UpdateTempPasswordResponse {
	active_token: string | null;
	refresh_token: string | null;
	is_first_time: boolean | null;
}

export async function updateTempPasswordRequest(
	args: Omit<UpdateTempPasswordRequest, "login">,
	login: string,
) {
	const response = await authRequest
		// The tokens in the answer sign the user in; like a login's, they are
		// issued against the gateway's default product unless the body names this one.
		.put<UpdateTempPasswordResponse>(
			AUTH_API_ROUTES.CHANGE_TEMP_PASSWORD(login),
			{ ...args, product: AUTH_PRODUCT },
		)
		.then((response) => response.data);
	return response;
}

export function useUpdateTempPasswordRequest() {
	const { login } = useAuth();
	const queryClient = useQueryClient();

	const { mutate, isPending } = useMutation<
		UpdateTempPasswordResponse,
		AxiosError,
		UpdateTempPasswordRequest,
		unknown
	>({
		mutationFn: ({ login, ...args }) => updateTempPasswordRequest(args, login),
		// The variables are both passwords — not kept once the form is gone.
		gcTime: 0,
		onSuccess: (data) => {
			const { active_token, refresh_token, is_first_time } = data;

			if (active_token && !is_first_time) {
				tokenInstance.setToken(active_token);
				if (refresh_token) tokenInstance.setRefreshToken(refresh_token);
				login();
				// After login(): the guest layout has already moved on, so
				// TempPassword's guard no longer looks for the credentials.
				queryClient.removeQueries({ queryKey: LOGIN_QUERY_KEY });
			}
		},
		onError: (err) => {
			handleApiError(err);
		},
	});

	return {
		updateTempPasswordMutate: mutate,
		updateTempPasswordPending: isPending,
	};
}
